import React, { useState } from "react";
import axios from "axios";
import { Pencil, Trash2, Wand2, Loader2, Download, Briefcase, GraduationCap, Globe, MapPin, CheckCircle2, Award, ExternalLink } from "lucide-react";
import { getAuthHeaders, useAuth } from "../context/AuthContext";
import { TimelineItem } from "./ProfessionalProfilePage";
import { ProfileHeaderCard } from "../components/profile/ProfileHeaderCard";
import { ProfileSectionCard } from "../components/profile/ProfileSectionCard";
import { EditSectionDrawer } from "../components/profile/EditSectionDrawer";
import { CandidateAvatar } from "../components/CandidateAvatar";
import { AvatarCropView } from "../components/AvatarCropView";
import { useAvatarCropper } from "../hooks/useAvatarCropper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_EXP = { company: "", position: "", start_date: "", end_date: "", current: false, description: "", location: "" };
const EMPTY_EDU = { institution: "", degree: "", field: "", year: "", location: "" };
const EMPTY_CERT = { name: "", issuer: "", issue_date: "", expiry_date: "", no_expiry: false, credential_id: "", credential_url: "" };
const LEVELS = ["native", "advanced", "intermediate", "basic"];
const CATEGORY_VALUES = ["Technology", "Finance", "Hospitality", "Healthcare", "Marketing", "Education", "Design", "Logistics"];
const SHIFT_VALUES = ["full-time", "part-time"];
const CONTRACT_VALUES = ["contract", "freelance", "internship"];
const WORK_MODE_VALUES = ["remote", "hybrid", "onsite"];
const AVAILABILITY_VALUES = ["immediate", "2_weeks", "1_month", "negotiable"];
const SKILLS_VISIBLE = 12;
const MAX_AVATAR_BYTES = 8 * 1024 * 1024;
const INDUSTRY_SUGGESTIONS = [
  "Tecnología", "Finanzas", "Hostelería", "Salud", "Marketing", "Educación", "Diseño", "Logística",
  "Retail", "Construcción", "Telecomunicaciones", "Turismo", "Automotriz", "Inmobiliaria",
  "Manufactura", "Energía", "Agricultura", "Legal", "Consultoría", "Recursos Humanos",
];
const CATEGORY_LABEL_KEYS = {
  Technology: "categoryTech", Finance: "categoryFinance", Hospitality: "categoryHospitality",
  Healthcare: "categoryHealthcare", Marketing: "categoryMarketing", Education: "categoryEducation",
  Design: "categoryDesign", Logistics: "categoryLogistics",
};
const JOB_TYPE_LABEL_KEYS = {
  "full-time": "typeFullTime", "part-time": "typePartTime",
  contract: "typeContract", freelance: "typeFreelance", internship: "typeInternship",
};
const availabilityLabelKey = (v) => `availability${v.replace(/_([a-z])/g, (m, c) => c.toUpperCase()).replace(/^([a-z0-9])/, (m, c) => c.toUpperCase())}`;

const inputStyle = { width: "100%", height: "44px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "0 12px", fontSize: "16px", fontFamily: "Poppins, sans-serif", color: "#0F172A", outline: "none", boxSizing: "border-box" };
const labelStyle = { display: "block", fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px", fontFamily: "Poppins, sans-serif" };
const chipStyle = (active) => ({
  padding: "8px 14px", borderRadius: "999px", border: `1.5px solid ${active ? "#028CF8" : "#E5E7EB"}`,
  background: active ? "#028CF8" : "white", color: active ? "white" : "#64748B",
  fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", cursor: "pointer", minHeight: "40px",
});

export default function ProfessionalProfileTab({ t, user, ownProfile, onRefetch }) {
  const { updateUser } = useAuth();
  const [toast, setToast] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [togglingAvailability, setTogglingAvailability] = useState(false);
  const [activeEditor, setActiveEditor] = useState(null); // { type, index }
  const [draft, setDraft] = useState({});
  const [skillsShowAll, setSkillsShowAll] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const [cropFile, setCropFile] = useState(null); // raw File pending the reposition/crop step
  const cropper = useAvatarCropper(cropFile);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const improveWithAI = async (text, onDone) => {
    if (!text?.trim()) { showToast(t("cv.addTextFirst")); return; }
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/cv/generate-section`, { section_type: "summary", input_text: text }, { headers: getAuthHeaders() });
      onDone(res.data.generated_text);
      showToast(t("cv.aiImproved"));
    } catch { showToast(t("errors.aiNotConfigured")); }
    finally { setAiLoading(false); }
  };

  if (!ownProfile) return null;
  const p = ownProfile;
  // Blank placeholder rows (no company/position, no institution/degree) must never
  // render as populated timeline items — mirrors backend _has_meaningful_entries().
  const experience = (p.experience || []).filter((e) => e.company?.trim() || e.position?.trim());
  const education = (p.education || []).filter((e) => e.institution?.trim() || e.degree?.trim());
  const skills = p.skills || [];
  const languages = p.languages || [];
  const certifications = p.certifications || [];
  const industries = p.industries || [];
  const prefs = p.preferences || {};
  const hasPrefs = !!(
    prefs.desired_categories?.length || prefs.desired_positions?.length || prefs.desired_locations?.length ||
    prefs.work_mode?.length || prefs.job_types?.length || prefs.salary_min_expectation ||
    prefs.availability || prefs.willing_to_relocate || prefs.willing_to_travel
  );

  const basePayload = () => ({
    name: p.name || "", title: p.title || "", location: p.location || "", phone: p.phone || "",
    email: p.email || "", linkedin: p.linkedin || "", summary: p.summary || "",
    experience, education, skills, languages,
    certifications: p.certifications || [], industries: p.industries || [],
    years_experience: p.years_experience || 0, preferences: prefs,
  });

  const savePatch = async (patch, usersPatch = null) => {
    setSaving(true);
    try {
      await axios.post(`${API}/cv`, { ...basePayload(), ...patch }, { headers: getAuthHeaders() });
      if (usersPatch) await axios.put(`${API}/my/profile`, usersPatch, { headers: getAuthHeaders() });
      await onRefetch();
      setActiveEditor(null);
      showToast(t("profTab.savedSuccess"));
    } catch { showToast(t("errors.unknownError")); }
    finally { setSaving(false); }
  };

  const toggleAvailability = async () => {
    setTogglingAvailability(true);
    try {
      await axios.put(`${API}/my/profile`, { open_to_work: !p.open_to_work }, { headers: getAuthHeaders() });
      await onRefetch();
    } catch { showToast(t("errors.unknownError")); }
    finally { setTogglingAvailability(false); }
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setAvatarError(t("profTab.avatarInvalidType")); return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setAvatarError(t("profTab.avatarTooLarge")); return;
    }
    setAvatarError("");
    e.target.value = ""; // allow re-selecting the exact same file later
    setCropFile(file); // → opens the reposition/crop step before it becomes avatarFile
  };

  const handleCropped = (blob) => {
    const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(blob));
    setCropFile(null);
  };
  const confirmAvatarCrop = async () => {
    const blob = await cropper.confirmCrop();
    if (blob) handleCropped(blob);
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return;
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", avatarFile);
      const res = await axios.post(`${API}/my/avatar`, formData, { headers: getAuthHeaders() });
      updateUser({ picture: res.data.picture });
      setAvatarFile(null); setAvatarPreview(null);
      await onRefetch();
      setActiveEditor(null);
      showToast(t("profTab.savedSuccess"));
    } catch (err) {
      setAvatarError(err?.response?.data?.detail || t("errors.unknownError"));
    } finally { setAvatarUploading(false); }
  };

  const deleteAvatar = async () => {
    setAvatarUploading(true);
    try {
      await axios.delete(`${API}/my/avatar`, { headers: getAuthHeaders() });
      updateUser({ picture: null });
      setAvatarFile(null); setAvatarPreview(null);
      await onRefetch();
      setActiveEditor(null);
      showToast(t("profTab.savedSuccess"));
    } catch { showToast(t("errors.unknownError")); }
    finally { setAvatarUploading(false); }
  };

  const openEditor = (type, index = null, initialDraft = {}) => { setDraft(initialDraft); setActiveEditor({ type, index }); };
  const openAvatarEditor = () => { setAvatarFile(null); setAvatarPreview(null); setAvatarError(""); setCropFile(null); setActiveEditor({ type: "avatar", index: null }); };
  const closeEditor = () => { setActiveEditor(null); setCropFile(null); };

  const missingHintKey = p.missing_sections?.[0]
    ? `completeCta${p.missing_sections[0].charAt(0).toUpperCase()}${p.missing_sections[0].slice(1)}`
    : null;

  return (
    <div className="space-y-4" data-testid="professional-profile-tab">
      {toast && (
        <div style={{ position: "fixed", top: "80px", right: "24px", zIndex: 9999, background: "#028CF8", color: "white", padding: "12px 20px", borderRadius: "12px", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", boxShadow: "0 4px 14px rgba(15,23,42,0.25)" }} data-testid="profile-toast">
          {toast}
        </div>
      )}

      <div>
        <ProfileHeaderCard
          t={t} profile={p} user={user}
          onAvatarClick={openAvatarEditor}
          onEditBasic={() => openEditor("basic", null, { name: p.name, title: p.title, location: p.location, phone: p.phone, linkedin: p.linkedin })}
          onOpenToWorkToggle={toggleAvailability}
          togglingAvailability={togglingAvailability}
          completionHintText={missingHintKey ? t(`profTab.${missingHintKey}`) : null}
          onCompleteProfile={() => {
            const first = p.missing_sections?.[0];
            if (first === "summary") openEditor("summary", null, { summary: p.summary || "" });
            else if (first === "preferences") openEditor("preferences", null, { ...prefs });
            else if (first === "basic_info") openEditor("basic", null, { name: p.name, title: p.title, location: p.location, phone: p.phone, linkedin: p.linkedin });
            else if (first === "industries") openEditor("industries", null, { list: [...industries] });
            else if (first === "photo") openAvatarEditor();
            else if (first === "experience") openEditor("experience", null, { ...EMPTY_EXP });
            else if (first === "education") openEditor("education", null, { ...EMPTY_EDU });
            else if (first === "skills") openEditor("skills", null, { list: [...skills] });
            else if (first === "languages") openEditor("languages", null, { list: [...languages], name: "", level: "native" });
            else if (first === "cv_document") window.location.href = "/cv-builder";
          }}
        />
      </div>

      {/* Contenido: 2 columnas eficientes en desktop (>=1024px) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Sobre mí */}
          <ProfileSectionCard title={t("profTab.aboutTitle")} testId="profile-summary-section"
            onAdd={() => openEditor("summary", null, { summary: p.summary || "" })}
            addLabel={p.summary ? t("profTab.editSummaryBtn") : t("profTab.addSummaryBtn")}
            isEmpty={!p.summary} emptyMessage={t("profTab.summaryEmpty")}
            emptyActionLabel={t("profTab.addSummaryBtn")} onEmptyAction={() => openEditor("summary", null, { summary: "" })}>
            <p className="text-sm text-[#374151] font-poppins leading-relaxed" data-testid="profile-summary-text">{p.summary}</p>
          </ProfileSectionCard>

          {/* Experiencia */}
          <ProfileSectionCard title={t("profTab.experienceTitle")} testId="profile-experience-section"
            onAdd={() => openEditor("experience", null, { ...EMPTY_EXP })} addLabel={t("profTab.addExperienceBtn")}
            isEmpty={experience.length === 0} emptyMessage={t("profTab.experienceEmpty")}
            emptyActionLabel={t("profTab.addExperienceBtn")} onEmptyAction={() => openEditor("experience", null, { ...EMPTY_EXP })}>
            <div>
              {experience.slice().reverse().map((exp, i) => {
                const idx = experience.length - 1 - i;
                return (
                  <div key={idx} className="flex items-start gap-2" data-testid={`profile-experience-item-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <TimelineItem title={exp.position} subtitle={exp.company}
                        dates={`${exp.start_date || ""} — ${exp.current ? t("profile.present") : (exp.end_date || "")}`}
                        description={[exp.location, exp.description].filter(Boolean).join(" · ")} />
                    </div>
                    <div className="flex gap-1 flex-shrink-0" style={{ marginTop: "4px" }}>
                      <button type="button" onClick={() => openEditor("experience", idx, { ...exp })} data-testid={`profile-experience-edit-${idx}`} aria-label={t("profTab.editExperienceTitle")}
                        className="h-7 w-7 rounded-lg border border-[#E5E7EB] flex items-center justify-center text-[#64748B] hover:bg-slate-50"><Pencil className="h-3 w-3" /></button>
                      <button type="button" onClick={() => { if (window.confirm(t("profTab.deleteExperienceConfirm"))) savePatch({ experience: experience.filter((_, j) => j !== idx) }); }}
                        data-testid={`profile-experience-delete-${idx}`} aria-label={t("profTab.delete")}
                        className="h-7 w-7 rounded-lg border border-[#FEE2E2] flex items-center justify-center text-[#EF4444] hover:bg-red-50"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ProfileSectionCard>

          {/* Educación */}
          <ProfileSectionCard title={t("profTab.educationTitle")} testId="profile-education-section"
            onAdd={() => openEditor("education", null, { ...EMPTY_EDU })} addLabel={t("profTab.addEducationBtn")}
            isEmpty={education.length === 0} emptyMessage={t("profTab.educationEmpty")}
            emptyActionLabel={t("profTab.addEducationBtn")} onEmptyAction={() => openEditor("education", null, { ...EMPTY_EDU })}>
            <div>
              {education.slice().reverse().map((edu, i) => {
                const idx = education.length - 1 - i;
                return (
                  <div key={idx} className="flex items-start gap-2" data-testid={`profile-education-item-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <TimelineItem title={edu.degree} subtitle={edu.institution} dates={edu.year} description={[edu.field, edu.location].filter(Boolean).join(" · ")} color="#028CF8" />
                    </div>
                    <div className="flex gap-1 flex-shrink-0" style={{ marginTop: "4px" }}>
                      <button type="button" onClick={() => openEditor("education", idx, { ...edu })} data-testid={`profile-education-edit-${idx}`} aria-label={t("profTab.editEducationTitle")}
                        className="h-7 w-7 rounded-lg border border-[#E5E7EB] flex items-center justify-center text-[#64748B] hover:bg-slate-50"><Pencil className="h-3 w-3" /></button>
                      <button type="button" onClick={() => { if (window.confirm(t("profTab.deleteEducationConfirm"))) savePatch({ education: education.filter((_, j) => j !== idx) }); }}
                        data-testid={`profile-education-delete-${idx}`} aria-label={t("profTab.delete")}
                        className="h-7 w-7 rounded-lg border border-[#FEE2E2] flex items-center justify-center text-[#EF4444] hover:bg-red-50"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ProfileSectionCard>
        </div>

        <div className="lg:col-span-1 space-y-4">
          {/* Skills */}
          <ProfileSectionCard title={t("profProfile.skills") || "Skills"} testId="profile-skills-section"
            onAdd={() => openEditor("skills", null, { list: [...skills] })} addLabel={t("profTab.addSkillBtn")}
            isEmpty={skills.length === 0} emptyMessage={t("profTab.skillsEmpty")}
            emptyActionLabel={t("profTab.addSkillBtn")} onEmptyAction={() => openEditor("skills", null, { list: [] })}>
            <div className="flex flex-wrap gap-2">
              {(skillsShowAll ? skills : skills.slice(0, SKILLS_VISIBLE)).map((s, i) => (
                <span key={i} data-testid={`profile-skill-chip-${i}`} style={{ padding: "6px 12px", borderRadius: "999px", background: "#F8FAFC", border: "1px solid #E5E7EB", fontSize: "12px", fontWeight: 600, color: "#374151", fontFamily: "Poppins, sans-serif" }}>{s}</span>
              ))}
              {skills.length > SKILLS_VISIBLE && (
                <button type="button" onClick={() => setSkillsShowAll(!skillsShowAll)} data-testid="profile-skills-toggle-all"
                  style={{ padding: "6px 12px", borderRadius: "999px", background: "white", border: "1px dashed #CBD5E1", fontSize: "12px", fontWeight: 700, color: "#028CF8", fontFamily: "Poppins, sans-serif", cursor: "pointer" }}>
                  {skillsShowAll ? t("profTab.showLessSkills") : t("profTab.showAllSkills").replace("{count}", skills.length - SKILLS_VISIBLE)}
                </button>
              )}
            </div>
          </ProfileSectionCard>

          {/* Industrias */}
          <ProfileSectionCard title={t("profTab.industriesTitle")} testId="profile-industries-section"
            onAdd={() => openEditor("industries", null, { list: [...industries] })} addLabel={t("profTab.addIndustryBtn")}
            isEmpty={industries.length === 0} emptyMessage={t("profTab.industriesEmpty")}
            emptyActionLabel={t("profTab.addIndustryBtn")} onEmptyAction={() => openEditor("industries", null, { list: [] })}>
            <div className="flex flex-wrap gap-2">
              {industries.map((s, i) => (
                <span key={i} data-testid={`profile-industry-chip-${i}`} style={{ padding: "6px 12px", borderRadius: "999px", background: "#F8FAFC", border: "1px solid #E5E7EB", fontSize: "12px", fontWeight: 600, color: "#374151", fontFamily: "Poppins, sans-serif" }}>{s}</span>
              ))}
            </div>
          </ProfileSectionCard>

          {/* Idiomas */}
          <ProfileSectionCard title={t("profProfile.languages") || "Idiomas"} testId="profile-languages-section"
            onAdd={() => openEditor("languages", null, { list: [...languages], name: "", level: "native" })} addLabel={t("profTab.addLanguageBtn")}
            isEmpty={languages.length === 0} emptyMessage={t("profTab.languagesEmpty")}
            emptyActionLabel={t("profTab.addLanguageBtn")} onEmptyAction={() => openEditor("languages", null, { list: [], name: "", level: "native" })}>
            <div className="flex flex-wrap gap-2">
              {languages.map((l, i) => (
                <span key={i} data-testid={`profile-language-chip-${i}`} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 12px", borderRadius: "999px", background: "#F8FAFC", border: "1px solid #E5E7EB", fontSize: "12px", fontWeight: 600, color: "#374151", fontFamily: "Poppins, sans-serif" }}>
                  <Globe className="h-3 w-3 text-[#94A3B8]" /> {l}
                </span>
              ))}
            </div>
          </ProfileSectionCard>

          {/* Certificaciones */}
          <ProfileSectionCard title={t("profTab.certificationsTitle")} testId="profile-certifications-section"
            onAdd={() => openEditor("certifications", null, { ...EMPTY_CERT })} addLabel={t("profTab.addCertificationBtn")}
            isEmpty={certifications.length === 0} emptyMessage={t("profTab.certificationsEmpty")}
            emptyActionLabel={t("profTab.addCertificationBtn")} onEmptyAction={() => openEditor("certifications", null, { ...EMPTY_CERT })}>
            <div className="space-y-3">
              {certifications.map((c, i) => (
                <div key={i} className="flex items-start justify-between gap-2" data-testid={`profile-certification-item-${i}`}>
                  <div style={{ minWidth: 0 }}>
                    <p className="text-sm font-bold text-[#0F172A] font-poppins truncate flex items-center gap-1.5">
                      <Award className="h-3.5 w-3.5 text-[#64748B] flex-shrink-0" /> {c.name}
                    </p>
                    <p className="text-xs text-[#64748B] font-poppins truncate">
                      {[c.issuer, c.no_expiry ? t("profTab.certNoExpiryLabel") : [c.issue_date, c.expiry_date].filter(Boolean).join(" – ")].filter(Boolean).join(" · ")}
                    </p>
                    {c.credential_url && (
                      <a href={c.credential_url} target="_blank" rel="noopener noreferrer" data-testid={`profile-certification-link-${i}`}
                        className="text-xs font-semibold text-[#028CF8] font-poppins inline-flex items-center gap-1 mt-0.5">
                        <ExternalLink className="h-3 w-3" /> {t("profTab.viewCredentialLink")}
                      </a>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button type="button" onClick={() => openEditor("certifications", i, { ...c })} data-testid={`profile-certification-edit-${i}`} aria-label={t("profTab.editCertificationTitle")}
                      className="h-7 w-7 rounded-lg border border-[#E5E7EB] flex items-center justify-center text-[#64748B] hover:bg-slate-50"><Pencil className="h-3 w-3" /></button>
                    <button type="button" onClick={() => { if (window.confirm(t("profTab.deleteCertificationConfirm"))) savePatch({ certifications: certifications.filter((_, j) => j !== i) }); }}
                      data-testid={`profile-certification-delete-${i}`} aria-label={t("profTab.delete")}
                      className="h-7 w-7 rounded-lg border border-[#FEE2E2] flex items-center justify-center text-[#EF4444] hover:bg-red-50"><Trash2 className="h-3 w-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          </ProfileSectionCard>

          {/* Preferencias laborales */}
          <ProfileSectionCard title={t("profTab.preferencesTitle")} testId="profile-preferences-section"
            onAdd={() => openEditor("preferences", null, { ...prefs })} addLabel={hasPrefs ? t("profTab.save") : t("profTab.addPreferencesBtn")}
            isEmpty={!hasPrefs} emptyMessage={t("profTab.preferencesEmpty")}
            emptyActionLabel={t("profTab.addPreferencesBtn")} onEmptyAction={() => openEditor("preferences", null, { ...prefs })}>
            <div className="flex flex-wrap gap-2" data-testid="profile-preferences-summary">
              {(prefs.desired_positions || []).map((s, i) => <span key={`pos-${i}`} style={chipStyle(false)}>{s}</span>)}
              {(prefs.desired_categories || []).map((c, i) => <span key={`cat-${i}`} style={chipStyle(true)}>{t(`jobsPage.${CATEGORY_LABEL_KEYS[c] || "categoryTech"}`)}</span>)}
              {(prefs.desired_locations || []).map((l, i) => <span key={`loc-${i}`} style={chipStyle(false)}><MapPin className="h-3 w-3 inline mr-1" />{l}</span>)}
              {(prefs.work_mode || []).map((c, i) => <span key={`wm-${i}`} style={chipStyle(false)}>{t(`jobsPage.workMode${c.charAt(0).toUpperCase()}${c.slice(1)}`)}</span>)}
              {(prefs.job_types || []).map((jt, i) => <span key={`jt-${i}`} style={chipStyle(false)}>{t(`jobCard.${JOB_TYPE_LABEL_KEYS[jt] || "typeFullTime"}`)}</span>)}
              {prefs.availability ? <span style={chipStyle(false)}>{t(`profTab.${availabilityLabelKey(prefs.availability)}`)}</span> : null}
              {prefs.salary_min_expectation ? <span style={chipStyle(false)}>€{prefs.salary_min_expectation}+</span> : null}
              {prefs.willing_to_relocate ? <span style={chipStyle(false)}>{t("profTab.willingToRelocateLabel")}</span> : null}
              {prefs.willing_to_travel ? <span style={chipStyle(false)}>{t("profTab.willingToTravelLabel")}</span> : null}
            </div>
          </ProfileSectionCard>

          {/* CV y documentos */}
          <ProfileSectionCard title={t("profTab.cvDocTitle")} testId="profile-cv-document-section"
            isEmpty={!p.has_cv} emptyMessage={t("profTab.cvDocEmpty")} emptyActionLabel={t("profTab.cvDocCreateBtn")}
            onEmptyAction={() => (window.location.href = "/cv-builder")}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                {p.last_updated && <p className="text-xs text-[#94A3B8] font-poppins">{t("profTab.cvDocUpdated")}: {new Date(p.last_updated).toLocaleDateString()}</p>}
              </div>
              <div className="flex gap-2 flex-wrap">
                <a href="/cv-builder" data-testid="profile-cv-view-link" style={{ height: "36px", padding: "0 14px", display: "flex", alignItems: "center", background: "white", color: "#028CF8", border: "1.5px solid #028CF8", borderRadius: "9px", fontSize: "12px", fontWeight: 700, fontFamily: "Poppins, sans-serif", textDecoration: "none" }}>{t("profTab.viewCvBtn")}</a>
              </div>
            </div>
          </ProfileSectionCard>
        </div>
      </div>

      {/* ── Drawer: Datos básicos ── */}
      {activeEditor?.type === "basic" && (
        <EditSectionDrawer open onOpenChange={closeEditor} saving={saving} title={t("profTab.basicInfoTitle")}
          onSave={() => savePatch({ name: draft.name, title: draft.title, location: draft.location, phone: draft.phone, linkedin: draft.linkedin },
            { name: draft.name, location: draft.location, phone: draft.phone, linkedin: draft.linkedin })}
          saveLabel={t("profTab.save")} cancelLabel={t("profTab.cancel")}>
          <div><label style={labelStyle}>{t("profTab.nameLabel")}</label><input data-testid="profile-field-name" style={inputStyle} value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
          <div><label style={labelStyle}>{t("profTab.titleFieldLabel")}</label><input data-testid="profile-field-title" style={inputStyle} placeholder={t("profTab.titleFieldPlaceholder")} value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></div>
          <div><label style={labelStyle}>{t("profTab.locationFieldLabel")}</label><input data-testid="profile-field-location" style={inputStyle} value={draft.location || ""} onChange={(e) => setDraft({ ...draft, location: e.target.value })} /></div>
          <div><label style={labelStyle}>{t("profTab.phoneFieldLabel")}</label><input data-testid="profile-field-phone" style={inputStyle} value={draft.phone || ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></div>
          <div><label style={labelStyle}>{t("profTab.linkedinFieldLabel")}</label><input data-testid="profile-field-linkedin" style={inputStyle} value={draft.linkedin || ""} onChange={(e) => setDraft({ ...draft, linkedin: e.target.value })} /></div>
        </EditSectionDrawer>
      )}

      {/* ── Drawer: Sobre mí ── */}
      {activeEditor?.type === "summary" && (
        <EditSectionDrawer open onOpenChange={closeEditor} saving={saving} title={t("profTab.aboutTitle")}
          onSave={() => savePatch({ summary: draft.summary })} saveLabel={t("profTab.save")} cancelLabel={t("profTab.cancel")}>
          <textarea data-testid="profile-field-summary" style={{ ...inputStyle, height: "160px", padding: "12px", resize: "vertical" }}
            placeholder={t("profTab.summaryPlaceholder")} value={draft.summary || ""} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} />
          <button type="button" disabled={aiLoading || !draft.summary?.trim()} data-testid="profile-summary-ai-button"
            onClick={() => improveWithAI(draft.summary, (text) => setDraft({ ...draft, summary: text }))}
            style={{ display: "flex", alignItems: "center", gap: "6px", height: "36px", padding: "0 14px", background: "#FDF2F8", color: "#F55FA5", border: "none", borderRadius: "9px", fontSize: "12px", fontWeight: 700, fontFamily: "Poppins, sans-serif", cursor: "pointer", opacity: aiLoading ? 0.6 : 1 }}>
            {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} {t("profTab.improveWithAI")}
          </button>
        </EditSectionDrawer>
      )}

      {/* ── Drawer: Experiencia ── */}
      {activeEditor?.type === "experience" && (
        <EditSectionDrawer open onOpenChange={closeEditor} saving={saving}
          title={activeEditor.index === null ? t("profTab.addExperienceTitle") : t("profTab.editExperienceTitle")}
          onDelete={activeEditor.index !== null ? () => { if (window.confirm(t("profTab.deleteExperienceConfirm"))) savePatch({ experience: experience.filter((_, j) => j !== activeEditor.index) }); } : null}
          deleteLabel={t("profTab.delete")}
          onSave={() => {
            const next = [...experience];
            if (activeEditor.index === null) next.push(draft); else next[activeEditor.index] = draft;
            savePatch({ experience: next });
          }} saveLabel={t("profTab.save")} cancelLabel={t("profTab.cancel")}>
          <div><label style={labelStyle}>{t("profTab.positionLabel")}</label><input data-testid="profile-exp-position" style={inputStyle} value={draft.position || ""} onChange={(e) => setDraft({ ...draft, position: e.target.value })} /></div>
          <div><label style={labelStyle}>{t("profTab.companyLabel")}</label><input data-testid="profile-exp-company" style={inputStyle} value={draft.company || ""} onChange={(e) => setDraft({ ...draft, company: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label style={labelStyle}>{t("profTab.startDateLabel")}</label><input data-testid="profile-exp-start" style={inputStyle} value={draft.start_date || ""} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} /></div>
            <div><label style={labelStyle}>{t("profTab.endDateLabel")}</label><input data-testid="profile-exp-end" style={inputStyle} disabled={draft.current} value={draft.end_date || ""} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#374151] font-poppins" style={{ minHeight: "44px" }}>
            <input data-testid="profile-exp-current" type="checkbox" checked={!!draft.current} onChange={(e) => setDraft({ ...draft, current: e.target.checked })} style={{ width: 18, height: 18 }} /> {t("profTab.currentJobLabel")}
          </label>
          <div><label style={labelStyle}>{t("profTab.experienceLocationLabel")}</label><input data-testid="profile-exp-location" style={inputStyle} value={draft.location || ""} onChange={(e) => setDraft({ ...draft, location: e.target.value })} /></div>
          <div><label style={labelStyle}>{t("profTab.descriptionLabel")}</label><textarea data-testid="profile-exp-description" style={{ ...inputStyle, height: "90px", padding: "12px", resize: "vertical" }} value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
        </EditSectionDrawer>
      )}

      {/* ── Drawer: Educación ── */}
      {activeEditor?.type === "education" && (
        <EditSectionDrawer open onOpenChange={closeEditor} saving={saving}
          title={activeEditor.index === null ? t("profTab.addEducationTitle") : t("profTab.editEducationTitle")}
          onDelete={activeEditor.index !== null ? () => { if (window.confirm(t("profTab.deleteEducationConfirm"))) savePatch({ education: education.filter((_, j) => j !== activeEditor.index) }); } : null}
          deleteLabel={t("profTab.delete")}
          onSave={() => {
            const next = [...education];
            if (activeEditor.index === null) next.push(draft); else next[activeEditor.index] = draft;
            savePatch({ education: next });
          }} saveLabel={t("profTab.save")} cancelLabel={t("profTab.cancel")}>
          <div><label style={labelStyle}>{t("profTab.degreeLabel")}</label><input data-testid="profile-edu-degree" style={inputStyle} value={draft.degree || ""} onChange={(e) => setDraft({ ...draft, degree: e.target.value })} /></div>
          <div><label style={labelStyle}>{t("profTab.institutionLabel")}</label><input data-testid="profile-edu-institution" style={inputStyle} value={draft.institution || ""} onChange={(e) => setDraft({ ...draft, institution: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label style={labelStyle}>{t("profTab.fieldLabel")}</label><input data-testid="profile-edu-field" style={inputStyle} value={draft.field || ""} onChange={(e) => setDraft({ ...draft, field: e.target.value })} /></div>
            <div><label style={labelStyle}>{t("profTab.yearLabel")}</label><input data-testid="profile-edu-year" style={inputStyle} value={draft.year || ""} onChange={(e) => setDraft({ ...draft, year: e.target.value })} /></div>
          </div>
          <div><label style={labelStyle}>{t("profTab.experienceLocationLabel")}</label><input data-testid="profile-edu-location" style={inputStyle} value={draft.location || ""} onChange={(e) => setDraft({ ...draft, location: e.target.value })} /></div>
        </EditSectionDrawer>
      )}

      {/* ── Drawer: Skills ── */}
      {activeEditor?.type === "skills" && (
        <EditSectionDrawer open onOpenChange={closeEditor} saving={saving} title={t("profTab.editSkillsTitle")}
          onSave={() => savePatch({ skills: draft.list })} saveLabel={t("profTab.save")} cancelLabel={t("profTab.cancel")}>
          <input data-testid="profile-skills-input" style={inputStyle} placeholder={t("profTab.skillsHint")}
            onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { setDraft({ ...draft, list: [...draft.list, e.target.value.trim()] }); e.target.value = ""; e.preventDefault(); } }} />
          <div className="flex flex-wrap gap-2">
            {(draft.list || []).map((s, i) => (
              <span key={i} data-testid={`profile-skills-draft-chip-${i}`} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 10px", borderRadius: "999px", background: "#F8FAFC", border: "1px solid #E5E7EB", fontSize: "12px", fontWeight: 600, color: "#374151", fontFamily: "Poppins, sans-serif" }}>
                {s}
                <button type="button" aria-label={t("profTab.delete")} onClick={() => setDraft({ ...draft, list: draft.list.filter((_, j) => j !== i) })} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: "14px", lineHeight: 1 }}>×</button>
              </span>
            ))}
          </div>
        </EditSectionDrawer>
      )}

      {/* ── Drawer: Idiomas ── */}
      {activeEditor?.type === "languages" && (
        <EditSectionDrawer open onOpenChange={closeEditor} saving={saving} title={t("profTab.editLanguagesTitle")}
          onSave={() => savePatch({ languages: draft.list })} saveLabel={t("profTab.save")} cancelLabel={t("profTab.cancel")}>
          <div className="flex gap-2">
            <input data-testid="profile-language-name-input" style={inputStyle} placeholder={t("profTab.languageNamePlaceholder")}
              value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <select data-testid="profile-language-level-select" style={{ ...inputStyle, width: "140px", flexShrink: 0 }} value={draft.level || "native"} onChange={(e) => setDraft({ ...draft, level: e.target.value })}>
              {LEVELS.map((lv) => <option key={lv} value={lv}>{t(`profTab.level${lv.charAt(0).toUpperCase()}${lv.slice(1)}`)}</option>)}
            </select>
          </div>
          <button type="button" data-testid="profile-language-add-button" disabled={!draft.name?.trim()}
            onClick={() => { setDraft({ ...draft, list: [...draft.list, `${draft.name.trim()} — ${t(`profTab.level${draft.level.charAt(0).toUpperCase()}${draft.level.slice(1)}`)}`], name: "" }); }}
            style={{ height: "38px", padding: "0 16px", background: "#EFF6FF", color: "#028CF8", border: "none", borderRadius: "9px", fontSize: "12px", fontWeight: 700, fontFamily: "Poppins, sans-serif", cursor: "pointer" }}>
            {t("profTab.addLanguageAction")}
          </button>
          <div className="flex flex-wrap gap-2">
            {(draft.list || []).map((l, i) => (
              <span key={i} data-testid={`profile-languages-draft-chip-${i}`} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 10px", borderRadius: "999px", background: "#F8FAFC", border: "1px solid #E5E7EB", fontSize: "12px", fontWeight: 600, color: "#374151", fontFamily: "Poppins, sans-serif" }}>
                {l}
                <button type="button" aria-label={t("profTab.delete")} onClick={() => setDraft({ ...draft, list: draft.list.filter((_, j) => j !== i) })} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: "14px", lineHeight: 1 }}>×</button>
              </span>
            ))}
          </div>
        </EditSectionDrawer>
      )}

      {/* ── Drawer: Preferencias laborales ── */}
      {activeEditor?.type === "preferences" && (
        <EditSectionDrawer open onOpenChange={closeEditor} saving={saving} title={t("profTab.editPreferencesTitle")}
          onSave={() => savePatch({ preferences: draft })} saveLabel={t("profTab.save")} cancelLabel={t("profTab.cancel")}>
          <div>
            <label style={labelStyle}>{t("profTab.desiredPositionsLabel")}</label>
            <input data-testid="profile-pref-positions-input" style={inputStyle} placeholder={t("profTab.desiredPositionsHint")}
              onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { setDraft({ ...draft, desired_positions: [...(draft.desired_positions || []), e.target.value.trim()] }); e.target.value = ""; e.preventDefault(); } }} />
            <div className="flex flex-wrap gap-2 mt-2">
              {(draft.desired_positions || []).map((s, i) => (
                <span key={i} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 10px", borderRadius: "999px", background: "#F8FAFC", border: "1px solid #E5E7EB", fontSize: "12px", fontFamily: "Poppins, sans-serif" }}>
                  {s}<button type="button" aria-label={t("profTab.delete")} data-testid={`profile-pref-position-remove-${i}`} onClick={() => setDraft({ ...draft, desired_positions: draft.desired_positions.filter((_, j) => j !== i) })} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8" }}>×</button>
                </span>
              ))}
            </div>
          </div>
          <div>
            <label style={labelStyle}>{t("profTab.desiredCategoriesLabel")}</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORY_VALUES.map((c) => {
                const active = (draft.desired_categories || []).includes(c);
                return <button key={c} type="button" data-testid={`profile-pref-category-${c}`} style={chipStyle(active)}
                  onClick={() => setDraft({ ...draft, desired_categories: active ? draft.desired_categories.filter((x) => x !== c) : [...(draft.desired_categories || []), c] })}>{t(`jobsPage.${CATEGORY_LABEL_KEYS[c]}`)}</button>;
              })}
            </div>
          </div>
          <div>
            <label style={labelStyle}>{t("profTab.desiredLocationsLabel")}</label>
            <input data-testid="profile-pref-locations-input" style={inputStyle} placeholder={t("profTab.desiredLocationsHint")}
              onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { setDraft({ ...draft, desired_locations: [...(draft.desired_locations || []), e.target.value.trim()] }); e.target.value = ""; e.preventDefault(); } }} />
            <div className="flex flex-wrap gap-2 mt-2">
              {(draft.desired_locations || []).map((s, i) => (
                <span key={i} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 10px", borderRadius: "999px", background: "#F8FAFC", border: "1px solid #E5E7EB", fontSize: "12px", fontFamily: "Poppins, sans-serif" }}>
                  <MapPin className="h-3 w-3 text-[#94A3B8]" />{s}<button type="button" aria-label={t("profTab.delete")} data-testid={`profile-pref-location-remove-${i}`} onClick={() => setDraft({ ...draft, desired_locations: draft.desired_locations.filter((_, j) => j !== i) })} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8" }}>×</button>
                </span>
              ))}
            </div>
          </div>
          <div>
            <label style={labelStyle}>{t("profTab.preferredModalityLabel")}</label>
            <div className="flex flex-wrap gap-2">
              {WORK_MODE_VALUES.map((v) => {
                const active = (draft.work_mode || []).includes(v);
                return <button key={v} type="button" data-testid={`profile-pref-workmode-${v}`} style={chipStyle(active)}
                  onClick={() => setDraft({ ...draft, work_mode: active ? draft.work_mode.filter((x) => x !== v) : [...(draft.work_mode || []), v] })}>{t(`jobsPage.workMode${v.charAt(0).toUpperCase()}${v.slice(1)}`)}</button>;
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={labelStyle}>{t("profTab.preferredShiftLabel")}</label>
              <div className="flex flex-wrap gap-2">
                {SHIFT_VALUES.map((v) => {
                  const active = (draft.job_types || []).includes(v);
                  return <button key={v} type="button" data-testid={`profile-pref-shift-${v}`} style={chipStyle(active)}
                    onClick={() => setDraft({ ...draft, job_types: active ? draft.job_types.filter((x) => x !== v) : [...(draft.job_types || []), v] })}>{t(`jobCard.type${v === "full-time" ? "FullTime" : "PartTime"}`)}</button>;
                })}
              </div>
            </div>
            <div>
              <label style={labelStyle}>{t("profTab.preferredContractLabel")}</label>
              <div className="flex flex-wrap gap-2">
                {CONTRACT_VALUES.map((v) => {
                  const active = (draft.job_types || []).includes(v);
                  const key = v === "contract" ? "Contract" : v === "freelance" ? "Freelance" : "Internship";
                  return <button key={v} type="button" data-testid={`profile-pref-contract-${v}`} style={chipStyle(active)}
                    onClick={() => setDraft({ ...draft, job_types: active ? draft.job_types.filter((x) => x !== v) : [...(draft.job_types || []), v] })}>{t(`jobCard.type${key}`)}</button>;
                })}
              </div>
            </div>
          </div>
          <div>
            <label style={labelStyle}>{t("profTab.availabilityLabel")}</label>
            <select data-testid="profile-pref-availability" style={inputStyle} value={draft.availability || ""} onChange={(e) => setDraft({ ...draft, availability: e.target.value })}>
              <option value="">{t("jobsPage.anyOption")}</option>
              {AVAILABILITY_VALUES.map((v) => <option key={v} value={v}>{t(`profTab.availability${v.replace(/_([a-z])/g, (m, c) => c.toUpperCase()).replace(/^([a-z0-9])/, (m, c) => c.toUpperCase())}`)}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>{t("profTab.salaryMinExpectationLabel")}</label>
            <input data-testid="profile-pref-salary" type="number" style={inputStyle} value={draft.salary_min_expectation || ""} onChange={(e) => setDraft({ ...draft, salary_min_expectation: e.target.value ? parseInt(e.target.value, 10) : null })} />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#374151] font-poppins" style={{ minHeight: "44px" }}>
            <input data-testid="profile-pref-relocate" type="checkbox" checked={!!draft.willing_to_relocate} onChange={(e) => setDraft({ ...draft, willing_to_relocate: e.target.checked })} style={{ width: 18, height: 18 }} /> {t("profTab.willingToRelocateLabel")}
          </label>
          <label className="flex items-center gap-2 text-sm text-[#374151] font-poppins" style={{ minHeight: "44px" }}>
            <input data-testid="profile-pref-travel" type="checkbox" checked={!!draft.willing_to_travel} onChange={(e) => setDraft({ ...draft, willing_to_travel: e.target.checked })} style={{ width: 18, height: 18 }} /> {t("profTab.willingToTravelLabel")}
          </label>
        </EditSectionDrawer>
      )}

      {/* ── Drawer: Foto de perfil ── */}
      {activeEditor?.type === "avatar" && (
        <EditSectionDrawer open onOpenChange={closeEditor} saving={avatarUploading}
          title={cropFile ? t("profTab.cropAvatarTitle") : t("profTab.avatarTitle")}
          onDelete={!cropFile && p.picture ? () => { if (window.confirm(t("profTab.deleteAvatarConfirm"))) deleteAvatar(); } : null}
          deleteLabel={t("profTab.delete")}
          onCancel={cropFile ? () => setCropFile(null) : closeEditor}
          onSave={() => (cropFile ? confirmAvatarCrop() : (avatarFile ? uploadAvatar() : closeEditor()))}
          saveDisabled={!!cropFile && !cropper.ready}
          saveLabel={t("profTab.save")} cancelLabel={t("profTab.cancel")}>
          {cropFile ? (
            <AvatarCropView cropper={cropper} t={t} />
          ) : (
            <div className="flex flex-col items-center gap-4">
              <CandidateAvatar name={p.name || user?.name} picture={avatarPreview || p.picture} size={96} />
              <label data-testid="profile-avatar-file-label" style={{ height: "40px", padding: "0 18px", display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#EFF6FF", color: "#028CF8", borderRadius: "10px", fontSize: "13px", fontWeight: 700, fontFamily: "Poppins, sans-serif", cursor: "pointer" }}>
                {t("profTab.changePhotoBtn")}
                <input type="file" accept="image/jpeg,image/png,image/webp" data-testid="profile-avatar-file-input" onChange={handleAvatarFileChange} style={{ display: "none" }} />
              </label>
              {avatarError && <p className="text-xs text-red-500 font-poppins" data-testid="profile-avatar-error">{avatarError}</p>}
            </div>
          )}
        </EditSectionDrawer>
      )}

      {/* ── Drawer: Certificación ── */}
      {activeEditor?.type === "certifications" && (
        <EditSectionDrawer open onOpenChange={closeEditor} saving={saving}
          title={activeEditor.index === null ? t("profTab.addCertificationTitle") : t("profTab.editCertificationTitle")}
          onDelete={activeEditor.index !== null ? () => { if (window.confirm(t("profTab.deleteCertificationConfirm"))) savePatch({ certifications: certifications.filter((_, j) => j !== activeEditor.index) }); } : null}
          deleteLabel={t("profTab.delete")}
          onSave={() => {
            if (!draft.name?.trim()) { showToast(t("profTab.certNameRequired")); return; }
            if (draft.credential_url?.trim() && !/^https?:\/\//i.test(draft.credential_url.trim())) { showToast(t("profTab.certInvalidUrl")); return; }
            const entry = { ...draft, name: draft.name.trim() };
            const next = [...certifications];
            if (activeEditor.index === null) next.push(entry); else next[activeEditor.index] = entry;
            savePatch({ certifications: next });
          }} saveLabel={t("profTab.save")} cancelLabel={t("profTab.cancel")}>
          <div><label style={labelStyle}>{t("profTab.certNameLabel")}</label><input data-testid="profile-cert-name" style={inputStyle} value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
          <div><label style={labelStyle}>{t("profTab.certIssuerLabel")}</label><input data-testid="profile-cert-issuer" style={inputStyle} value={draft.issuer || ""} onChange={(e) => setDraft({ ...draft, issuer: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label style={labelStyle}>{t("profTab.certIssueDateLabel")}</label><input data-testid="profile-cert-issue-date" style={inputStyle} value={draft.issue_date || ""} onChange={(e) => setDraft({ ...draft, issue_date: e.target.value })} /></div>
            <div><label style={labelStyle}>{t("profTab.certExpiryDateLabel")}</label><input data-testid="profile-cert-expiry-date" style={inputStyle} disabled={!!draft.no_expiry} value={draft.expiry_date || ""} onChange={(e) => setDraft({ ...draft, expiry_date: e.target.value })} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#374151] font-poppins" style={{ minHeight: "44px" }}>
            <input data-testid="profile-cert-no-expiry" type="checkbox" checked={!!draft.no_expiry} onChange={(e) => setDraft({ ...draft, no_expiry: e.target.checked, expiry_date: e.target.checked ? "" : draft.expiry_date })} style={{ width: 18, height: 18 }} /> {t("profTab.certNoExpiryLabel")}
          </label>
          <div><label style={labelStyle}>{t("profTab.certCredentialIdLabel")}</label><input data-testid="profile-cert-credential-id" style={inputStyle} value={draft.credential_id || ""} onChange={(e) => setDraft({ ...draft, credential_id: e.target.value })} /></div>
          <div><label style={labelStyle}>{t("profTab.certCredentialUrlLabel")}</label><input data-testid="profile-cert-credential-url" style={inputStyle} placeholder="https://" value={draft.credential_url || ""} onChange={(e) => setDraft({ ...draft, credential_url: e.target.value })} /></div>
        </EditSectionDrawer>
      )}

      {/* ── Drawer: Industrias ── */}
      {activeEditor?.type === "industries" && (
        <EditSectionDrawer open onOpenChange={closeEditor} saving={saving} title={t("profTab.editIndustriesTitle")}
          onSave={() => savePatch({ industries: draft.list })} saveLabel={t("profTab.save")} cancelLabel={t("profTab.cancel")}>
          <input data-testid="profile-industries-input" style={inputStyle} placeholder={t("profTab.industriesHint")} list="industry-suggestions"
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value.trim()) {
                const val = e.target.value.trim();
                if (!draft.list.some((x) => x.toLowerCase() === val.toLowerCase())) setDraft({ ...draft, list: [...draft.list, val] });
                e.target.value = ""; e.preventDefault();
              }
            }} />
          <datalist id="industry-suggestions">
            {INDUSTRY_SUGGESTIONS.map((i) => <option key={i} value={i} />)}
          </datalist>
          <div className="flex flex-wrap gap-2">
            {(draft.list || []).map((s, i) => (
              <span key={i} data-testid={`profile-industries-draft-chip-${i}`} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 10px", borderRadius: "999px", background: "#F8FAFC", border: "1px solid #E5E7EB", fontSize: "12px", fontWeight: 600, color: "#374151", fontFamily: "Poppins, sans-serif" }}>
                {s}
                <button type="button" aria-label={t("profTab.delete")} data-testid={`profile-industries-remove-${i}`} onClick={() => setDraft({ ...draft, list: draft.list.filter((_, j) => j !== i) })} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: "14px", lineHeight: 1 }}>×</button>
              </span>
            ))}
          </div>
        </EditSectionDrawer>
      )}
    </div>
  );
}
